import { Page } from 'components/Page/Page';
import i18n from 'i18n';
import { Nav } from 'react-bootstrap';
import { ChannelGroupTabs } from '../ChannelGroupForm/ChannelGroupFormModel';
import { useChannelGroupDetailModel } from './ChannelGroupDetailModel';
import styles from './channelGroupDetail.module.scss';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { useCallback, useMemo } from 'react';
import { compact, defaultTo, get } from 'lodash';
import { InfoDisplaySection } from 'components/InfoDisplaySection/InfoDisplaySection';
import { Currency } from 'core';
import { ChannelGroupBidPriceSettingInfo } from 'components/BidPrice/ChannelGroupBidPriceSettingInfo';
import { CustomField } from 'components/common/form/field/CustomField';
import { Link } from 'react-router-dom';
import { ChannelGroupMembers } from './ChannelGroupMembers';
import {
  ChannelGroupStatus,
  ChannelType,
  DynamicCampaignAdTypeSetting
} from 'core/channelGroup/ChannelGroup';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import {
  getCompositeCreativeError,
  getCompositeCreativeText,
  getCompositePixelSizeError,
  getCompositePixelSizeText
} from 'core/channelGroup/compositeSettingDisplay';
import { Modal } from 'components/common/Modal/Modal';
import { Formik, FormikProps } from 'formik';
import { FormikField } from 'components/common/form/field/FormikField';
import { validateEmpty } from 'utils/ValidateUtils';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { isRoles } from 'core/permission/PermissionDSL';
import { RoleNames } from 'core';
import { AuditLog } from 'containers/AuditLog/AuditLog';

/**
 * ECOPD-5701：停用理由字數上限。單位是 UTF-16 code unit（中文 1 字 = 1），
 * 對齊後端 Java `String.length()`。
 *
 * ⚠️ **不要**改用 `validateStringMaxLength` —— 它底層的 `getByteLength` 中文算 2，
 * 會在 250 個中文字就擋下來，剛好是允許長度的一半。
 */
export const ARCHIVE_REASON_MAX_LENGTH = 500;

/**
 * ECOPD-5722：`InfoDisplaySection` 的 `extra` 預設是灰字說明的樣式，跟純資訊提示長得一樣。
 * 合成設定有誤是要讓系統管理員看見的錯誤，所以自己上警示色，與渠道編輯頁一致。
 */
const renderCompositeSettingError = (error?: string) =>
  error ? (
    <span className={styles.compositeSettingError}>{error}</span>
  ) : undefined;

export type ChannelGroupDetailProps = {
  /** ECOPD-5572：停用 / 重新啟用成功後通知外層重抓渠道清單。 */
  onChannelGroupChanged?: () => void;
};

export const ChannelGroupDetail: React.FC<ChannelGroupDetailProps> = ({
  onChannelGroupChanged
}) => {
  const {
    loading,
    tab,
    channelGroup,
    breadcrumbsRoutes,
    agencyOptions,
    onTabChange,
    pendingArchiveAction,
    requestArchive,
    requestUnarchive,
    confirmArchiveAction,
    cancelArchiveAction,
    auditLogModel
  } = useChannelGroupDetailModel(onChannelGroupChanged);

  const infoMarginTop = useMemo(() => ({ marginTop: '30px' }), []);

  // ECOPD-5722: 合成設定另外取，不要併進各段 screenProperty 的 fallback 物件——
  // isCompositeCreative 是三態，補一個 false 進去就分不出「RMax 還沒同步」了。
  //
  // 基本資訊與素材尺寸兩個分頁都要用，所以放在這一層。出現條件與後端輸出
  // compositeSettingStatus 的條件一致，兩種都算：
  //   1. 連台渠道
  //   2. 連台已經關掉，但底下還留著連台素材類型
  // 少了第 2 種，那種渠道的素材會被擋下來，系統管理員卻在這一頁看不到任何異常。
  const compositeSettingStatus =
    channelGroup?.screenProperty?.compositeSettingStatus;
  const showCompositeSetting =
    !!channelGroup?.screenProperty?.isScreenSpliced ||
    compositeSettingStatus !== undefined;

  const basicInfo = useMemo(() => {
    // 僅影片型 outdoor（OUTDOOR/MOVABLE_OUTDOOR）有 allowedVideoCodecs；
    // Beacon/RTB 恆為 []（RMax 定義），不顯示編碼列。
    // ECOPD-5581: E_PAPER 刻意不列入——電子紙只投圖片（沒有喇叭、supportAudio 恆為
    // false），「支援的影片編碼」對它沒有意義。而且下面那段在 allowedVideoCodecs
    // 為空時會顯示「所有」，對一個根本不支援影片的渠道等於講反話。
    // 上線後這一列會從 prod 的電子紙渠道消失，那是正確的。
    const isOutdoorChannel =
      !!channelGroup &&
      [ChannelType.OUTDOOR, ChannelType.MOVABLE_OUTDOOR].includes(
        channelGroup.channelType
      );
    const {
      isScreenSpliced,
      isMultiScreenSameVideo,
      screenSplicedVerticalScreenCount,
      screenSplicedHorizontalScreenCount
    } = channelGroup?.screenProperty || {
      isScreenSpliced: false,
      isMultiScreenSameVideo: false,
      screenSplicedVerticalScreenCount: 0,
      screenSplicedHorizontalScreenCount: 0
    };
    const verticalScreenUnit =
      screenSplicedVerticalScreenCount > 1
        ? i18n.t('common.units.devices')
        : i18n.t('common.units.device');
    const horizontalScreenUnit =
      screenSplicedHorizontalScreenCount > 1
        ? i18n.t('common.units.devices')
        : i18n.t('common.units.device');
    const alertEmails = defaultTo(channelGroup?.alertEmails, []);

    return compact([
      {
        label: i18n.t('channelGroup.labels.name'),
        value: defaultTo(channelGroup?.name, '')
      },
      channelGroup?.addonFeatures.campaign.limitPublisherId && {
        label: i18n.t('channelGroup.labels.publisherIds'),
        value: defaultTo(channelGroup?.publisherIds, []).join(', ')
      },
      {
        label: i18n.t('channelGroup.labels.isMultiScreenSameVideo'),
        value: isMultiScreenSameVideo
          ? i18n.t('common.labels.can')
          : i18n.t('common.labels.cannot')
      },
      {
        label: i18n.t('channelGroup.labels.isScreenSpliced'),
        value: isScreenSpliced
          ? i18n.t('common.labels.can')
          : i18n.t('common.labels.cannot')
      },
      isScreenSpliced && {
        label: i18n.t('channelGroup.labels.screenSplicedVerticalScreenCount'),
        value: `${screenSplicedVerticalScreenCount} ${verticalScreenUnit}`
      },
      isScreenSpliced && {
        label: i18n.t('channelGroup.labels.screenSplicedHorizontalScreenCount'),
        value: `${screenSplicedHorizontalScreenCount} ${horizontalScreenUnit}`
      },
      // ECOPD-5722: 合成設定由 RMax 同步而來，PMax 唯讀。「還沒同步」講的是這個旗標
      // 本身，所以錯誤掛在這一列；尺寸相關的三句掛在素材尺寸分頁的那一列上。
      showCompositeSetting && {
        label: i18n.t('channelGroup.labels.isCompositeCreative'),
        value: getCompositeCreativeText(channelGroup?.screenProperty),
        extra: renderCompositeSettingError(
          getCompositeCreativeError(compositeSettingStatus)
        )
      },
      {
        label: i18n.t('channelGroup.labels.supportPMP'),
        value: channelGroup?.addonFeatures.campaign.supportPMP
          ? i18n.t('common.labels.yes')
          : i18n.t('common.labels.no')
      },
      // ECOPD-5629: RTB / FB 內建 CBO 且讀不到 addonFeatures ⇒ 恆顯示「是」；
      // 其餘渠道看旗標。
      {
        label: i18n.t('channelGroup.labels.supportCBO'),
        value:
          (!!channelGroup &&
            ChannelGroupSetting.isCboBuiltInChannelGroup(channelGroup.id)) ||
          channelGroup?.addonFeatures.campaign.supportCBO
            ? i18n.t('common.labels.yes')
            : i18n.t('common.labels.no')
      },
      channelGroup?.deliveryUnit && {
        label: i18n.t('channelGroup.labels.deliveryUnit'),
        value: i18n.t(`deliveryUnit.${channelGroup.deliveryUnit.toLowerCase()}`)
      },
      // ECOPD-5581: 元太系統的 site 識別碼，只有電子紙渠道有。值由 RMax 同步寫入，
      // PMax 這側唯讀。條件看 channelType 而不是「siteId 有沒有值」——電子紙渠道
      // 沒同步到 siteId 是要讓人看見的異常，不是把整列藏起來。
      channelGroup?.channelType === ChannelType.E_PAPER && {
        label: i18n.t('channelGroup.labels.siteId'),
        value:
          channelGroup?.property?.epaperProps?.siteId ||
          i18n.t('common.labels.noData')
      },
      // ECOPD-5470: 唯讀顯示此渠道支援的影片編碼（H264/H265）。
      // 為空＝不限制 → 顯示「所有」（見上方 isOutdoorChannel 註解）。
      isOutdoorChannel && {
        label: i18n.t('channelGroup.labels.allowedVideoCodecs'),
        value: channelGroup?.allowedVideoCodecs?.length
          ? channelGroup?.allowedVideoCodecs?.join(', ')
          : i18n.t('channelGroup.labels.allowedVideoCodecsAll')
      },
      {
        label: i18n.t('channelGroup.labels.alertEmails'),
        value:
          alertEmails.length > 0
            ? alertEmails.join('\n')
            : i18n.t('common.labels.noData')
      },
      // ECOPD-5572：停用狀態要看得到，否則使用者按完停用鈕，畫面上除了按鈕文案變了
      // 之外沒有任何地方顯示這個渠道已經停用。
      // 讀 `effectiveArchived`（合併判定）—— 對使用者而言「被誰停的」不重要，
      // 「現在是不是停用」才重要。
      {
        label: i18n.t('channelGroupDetail.labels.archiveStatus'),
        value: channelGroup?.effectiveArchived
          ? i18n.t('channelGroupDetail.labels.archiveStatusArchived')
          : i18n.t('channelGroupDetail.labels.archiveStatusActive')
      },
      // ECOPD-5701：兩個來源各自有值就各印一行，判定用 truthy（沒有理由時 key
      // 整個不存在）。兩者皆無 → 整列不顯示，這是合法狀態不是錯誤。
      // 與列表不同：**這裡不截斷**，詳情頁就是要看全文。
      (!!channelGroup?.pmaxArchiveReason ||
        !!channelGroup?.rmaxArchiveReason) && {
        label: i18n.t('channelGroupDetail.labels.archiveReason'),
        value: (
          <>
            {channelGroup?.pmaxArchiveReason && (
              <div>
                {i18n.t('channelGroupList.labels.archiveReasonPmax', {
                  reason: channelGroup.pmaxArchiveReason
                })}
              </div>
            )}
            {channelGroup?.rmaxArchiveReason && (
              <div>
                {i18n.t('channelGroupList.labels.archiveReasonRmax', {
                  reason: channelGroup.rmaxArchiveReason
                })}
              </div>
            )}
          </>
        )
      }
    ]);
  }, [channelGroup, compositeSettingStatus, showCompositeSetting]);

  const specialSettingsInfo = useMemo(
    () => [
      {
        label: i18n.t('channelGroupForm.labels.supportWeatherTargeting'),
        value: channelGroup?.addonFeatures.campaign.supportWeatherTargeting
          ? i18n.t('common.labels.yes')
          : i18n.t('common.labels.no')
      }
    ],
    [channelGroup]
  );

  const reportInfo = useMemo(() => {
    const settings = get(channelGroup, 'addonFeatures.report', {});
    return Object.keys(settings)
      .filter((key) => settings[key])
      .map((key) => {
        return {
          label: i18n.t(`channelGroupForm.labels.${key}`),
          value: undefined
        };
      });
  }, [channelGroup]);

  const derecognitionAgencyInfo = useMemo(() => {
    const agencyIds = get(
      channelGroup,
      'property.addonProps.derecognitionAgencyIds',
      []
    );
    const agencyNames = agencyIds.map((id) => {
      const option = agencyOptions.find((option) => option.value === id);
      return option ? option.label : id;
    });
    return [
      {
        label: '',
        value: agencyNames.join('\n')
      }
    ];
  }, [channelGroup, agencyOptions]);

  const currencyInfo = useMemo(() => {
    const currency = get(
      channelGroup,
      'property.addonProps.currency',
      Currency.NTD
    );
    return [
      {
        label: '',
        value: i18n.t(`common.currency.${currency.toLowerCase()}`)
      }
    ];
  }, [channelGroup]);

  const defaultMarginInfo = useMemo(() => {
    const rate = get(channelGroup, 'property.addonProps.defaultMargin', 0);
    return [
      {
        label: '',
        value: rate
      }
    ];
  }, [channelGroup]);

  const nonExpressPassCategoriesInfo = useMemo(() => {
    const categoryKeys: string[] = get(
      channelGroup,
      'property.addonProps.nonExpressPassCategories',
      []
    );
    const categoryLabels = categoryKeys.map((key) =>
      i18n.t(`tenmaxCategory.labels.${key}`)
    );
    return [
      {
        label: '',
        value: categoryLabels.join('\n')
      }
    ];
  }, [channelGroup]);

  const outdoorCreativeSizesInfo = useMemo(() => {
    const outdoorCreativeSizes: {
      width: number;
      height: number;
    }[] = get(channelGroup, 'property.addonProps.outdoorCreativeSizes', []);

    const {
      isScreenSpliced,
      screenSplicedVerticalScreenCount: verticalScreenCount,
      screenSplicedHorizontalScreenCount: horizontalScreenCount
    } = channelGroup?.screenProperty || {
      isScreenSpliced: false,
      screenSplicedVerticalScreenCount: 1,
      screenSplicedHorizontalScreenCount: 1
    };

    const outdoorCreativeSize = outdoorCreativeSizes[0];
    const creativeSizes: {
      label: string;
      value: string;
      extra?: React.ReactNode;
    }[] = [];

    if (outdoorCreativeSize) {
      if (isScreenSpliced) {
        creativeSizes.push({
          label: i18n.t('channelGroup.labels.multiSplicedScreenCreativeSize'),
          value: `${outdoorCreativeSize.width * horizontalScreenCount} x ${outdoorCreativeSize.height * verticalScreenCount}`
        });
      }
      creativeSizes.push({
        label: i18n.t('channelGroup.labels.singleScreenCreativeSize'),
        value: `${outdoorCreativeSize.width} x ${outdoorCreativeSize.height}`
      });
    }

    // ECOPD-5722: 實際播放尺寸跟其他兩個尺寸擺在一起。出現條件見 basicInfo 那段的說明，
    // 與後端輸出 compositeSettingStatus 的條件一致，不看 outdoorCreativeSize 有沒有值。
    if (showCompositeSetting) {
      creativeSizes.push({
        label: i18n.t('channelGroup.labels.compositePixelSize'),
        value: getCompositePixelSizeText(channelGroup?.screenProperty),
        extra: renderCompositeSettingError(
          getCompositePixelSizeError(compositeSettingStatus)
        )
      });
    }
    return creativeSizes;
  }, [channelGroup, compositeSettingStatus, showCompositeSetting]);

  const renderBidPriceSettings = useCallback(() => {
    const bidPriceSettings = get(
      channelGroup,
      'property.addonProps.campaignBidPrice',
      []
    ).map((bidPriceSetting) => {
      return (
        <ChannelGroupBidPriceSettingInfo
          key={bidPriceSetting.type}
          label=''
          bidPriceSetting={bidPriceSetting}
          currency={get(
            channelGroup,
            'property.addonProps.currency',
            Currency.NTD
          )}
        />
      );
    });
    return (
      <CustomField label='' name='bidPriceSettings'>
        <div style={infoMarginTop}>{bidPriceSettings}</div>
      </CustomField>
    );
  }, [channelGroup, infoMarginTop]);

  // 戶外渠道的品牌設定。沒填的欄位顯示「無資料」而不是整列藏起來，
  // 方便看出哪些渠道還沒補齊。
  const brandInfo = useMemo(() => {
    if (
      !channelGroup ||
      !ChannelGroupSetting.isOutdoorChannelType(channelGroup.channelType)
    ) {
      return undefined;
    }
    const brand = channelGroup.property.addonProps.brand;
    const noData = i18n.t('common.labels.noData');
    const renderText = (value?: string | null) => value?.trim() || noData;
    const renderIcon = (url: string | null | undefined, alt: string) =>
      url ? <img className={styles.brandIcon} src={url} alt={alt} /> : noData;
    const url = brand?.url?.trim();
    const filteredIconLabel = i18n.t(
      'channelGroupForm.labels.brandFilteredIcon'
    );
    const normalIconLabel = i18n.t('channelGroupForm.labels.brandNormalIcon');
    return [
      {
        label: i18n.t('channelGroupForm.labels.brandNameI18n.ja'),
        value: renderText(brand?.nameI18n?.ja)
      },
      {
        label: i18n.t('channelGroupForm.labels.brandNameI18n.en'),
        value: renderText(brand?.nameI18n?.en)
      },
      {
        label: i18n.t('channelGroupForm.labels.brandNameI18n.zh-tw'),
        value: renderText(brand?.nameI18n?.['zh-TW'])
      },
      {
        label: i18n.t('channelGroupForm.labels.brandUrl'),
        value: url ? (
          <a href={url} target='_blank' rel='noopener noreferrer'>
            {url}
          </a>
        ) : (
          noData
        )
      },
      {
        label: filteredIconLabel,
        value: renderIcon(brand?.filteredIconUrl, filteredIconLabel)
      },
      {
        label: normalIconLabel,
        value: renderIcon(brand?.normalIconUrl, normalIconLabel)
      }
    ];
  }, [channelGroup]);

  // ECOPD-5581: 電子紙渠道的預設圖片（裝置沒廣告可投時的填充圖），唯讀預覽。
  // 只顯示圖片本身 —— 元太的 image UUID 是內部識別碼，使用者不需要看到。
  const renderEpaperDefaultImage = useCallback(() => {
    const url = channelGroup?.property?.epaperProps?.epaperDefaultImageUrl;
    return (
      <CustomField label='' name='epaperDefaultImage'>
        <div style={infoMarginTop}>
          {url ? (
            <img
              className={styles.epaperDefaultImage}
              src={url}
              alt={i18n.t('channelGroupForm.labels.epaperDefaultImage')}
            />
          ) : (
            i18n.t('common.labels.noData')
          )}
        </div>
      </CustomField>
    );
  }, [channelGroup, infoMarginTop]);

  const adTypesSettings = useMemo(() => {
    const adTypeSettings: DynamicCampaignAdTypeSetting[] = get(
      channelGroup,
      'campaignAdTypeSettings',
      []
    );
    return [
      {
        label: '',
        value: adTypeSettings.map((setting) => setting.name).join('\n')
      }
    ];
  }, [channelGroup]);

  /**
   * ECOPD-5572：停用 / 重新啟用入口，三種狀態各自不同：
   *
   *   RMax 已封存  → **不顯示按鈕**。PMax 這邊沒有可做的動作 —— 就算解除了自停，
   *                  `effectiveArchived` 仍是 true，按鈕只會給出「按了有用」的錯覺。
   *   PMax 自停    → 顯示「重新啟用」
   *   兩者皆無     → 顯示「停用」
   *
   * 兩種來源同時成立時走第一條（不顯示）：RMax 那側還封存著，解除自停不會讓渠道
   * 回來，所以入口先收起來，等 RMax 解除封存後才會再出現。
   *
   * ⚠️ 這裡讀 `status` 是在問「**RMax** 那側封不封存」這個單一問題，
   * **不是**在重算「這個渠道是否停用」—— 那個判定一律讀後端算好的 `effectiveArchived`。
   *
   * 權限走既有的 `isRoles(sysAdmin)`。這條 route 本來就只開給 sysAdmin，
   * 這裡再包一層是為了讓意圖留在程式碼裡。
   * ⚠️ **不要**改成 `addOnEnabled(...)` —— `isFeatureEnable` 對 system admin 永遠回 false，
   * 按鈕會對唯一該看到它的人隱形，而且測試不一定抓得到。
   */
  const renderArchiveButton = () => {
    if (channelGroup?.status === ChannelGroupStatus.ARCHIVED) {
      return null;
    }
    const archived = !!channelGroup?.pmaxArchived;
    return (
      <PermissionChecker permissionAware={isRoles(RoleNames.sysAdmin)}>
        <a onClick={archived ? requestUnarchive : requestArchive}>
          {archived
            ? i18n.t('channelGroupDetail.buttons.unarchive')
            : i18n.t('channelGroupDetail.buttons.archive')}
        </a>
      </PermissionChecker>
    );
  };

  /**
   * 確認框。停用那一版**必須**講明三件不可逆的後果（會暫停活動、會刪掉 RMax 端的
   * PMP deal、重新啟用不會恢復）—— 這是整個功能唯一的誤按防線。
   * 文案用 `\n` 分段，這裡逐行渲染。
   */
  const renderArchiveConfirmModal = () => {
    if (!pendingArchiveAction) {
      return null;
    }
    const isArchiving = pendingArchiveAction === 'archive';
    const content = i18n.t(
      isArchiving
        ? 'channelGroupDetail.labels.archiveConfirmContent'
        : 'channelGroupDetail.labels.unarchiveConfirmContent'
    );
    const warningLines = content
      .split('\n')
      .map((line) => <div key={line}>{line}</div>);
    const title = i18n.t(
      isArchiving
        ? 'channelGroupDetail.labels.archiveConfirmTitle'
        : 'channelGroupDetail.labels.unarchiveConfirmTitle'
    );

    // 重新啟用沒有理由可填（端點無 body），維持原本的純文字確認框。
    if (!isArchiving) {
      return (
        <Modal
          title={title}
          primaryButton={{
            title: i18n.t('common.buttons.confirm'),
            callback: () => confirmArchiveAction(),
            // 與停用那一版對稱：請求飛在路上時不讓使用者再按一次。
            disabled: loading
          }}
          secondaryButton={{
            title: i18n.t('common.buttons.cancel'),
            callback: cancelArchiveAction
          }}
          dismiss={cancelArchiveAction}
        >
          {warningLines}
        </Modal>
      );
    }

    // ECOPD-5701：停用要填理由。
    //
    // 版面順序是刻意的：**輸入欄在上、警告在下**。那三行警告是唯一的誤按防線，
    // 要離「確定」越近越好，使用者的最後一眼該落在警告上。
    //
    // 按鈕**不因驗證失敗而** disable：要能按下去才知道為什麼不能送，改由 validate 擋。
    // ⚠️ 這跟下面的 `disabled: loading` 是兩回事（那行擋的是請求飛在路上時重複送出），
    // 不要看到這句就把那行刪掉。
    return (
      <Formik
        initialValues={{ reason: '' }}
        onSubmit={(values) => confirmArchiveAction(values.reason)}
        validate={(values) => {
          // trim 後為空才算未填（後端對純空白回 400）。
          const error = validateEmpty(values.reason?.trim());
          return error ? { reason: error } : {};
        }}
      >
        {(formProps: FormikProps<{ reason: string }>) => (
          <Modal
            title={title}
            primaryButton={{
              title: i18n.t('common.buttons.confirm'),
              callback: formProps.submitForm,
              disabled: loading
            }}
            secondaryButton={{
              title: i18n.t('common.buttons.cancel'),
              callback: cancelArchiveAction
            }}
            dismiss={cancelArchiveAction}
          >
            <FormikField.TextArea
              name='reason'
              label={i18n.t('channelGroupDetail.labels.archiveReason')}
              rows={3}
              // ⚠️ 這兩個 prop 都是為了這個窄 modal，不要拿掉：
              // `fieldContentWidth` 預設寫死 424px，在 500px 的 modal 裡會溢出右緣；
              // 但光改 100% 還不夠，`.fieldContent` 的 1fr 兄弟軌會吃掉一半（剩 182px），
              // 要靠 `formGroupClassName` 的樣式壓成單欄。
              formGroupClassName={styles.archiveReasonField}
              fieldContentWidth='100%'
              maxLength={ARCHIVE_REASON_MAX_LENGTH}
              placeholder={i18n.t(
                'channelGroupDetail.placeholders.archiveReason'
              )}
              hint={i18n.t('formValidate.labels.remainingCharacters', {
                num:
                  ARCHIVE_REASON_MAX_LENGTH -
                  (formProps.values.reason?.length ?? 0)
              })}
            />
            {warningLines}
          </Modal>
        )}
      </Formik>
    );
  };

  if (!channelGroup) {
    return <LoadingIndicator />;
  }

  return (
    <div style={{ height: '100%' }}>
      {loading && <LoadingIndicator />}
      {renderArchiveConfirmModal()}
      <Page
        title={i18n.t('channelGroups.title')}
        breadcrumbsRoutes={breadcrumbsRoutes}
        topAreaEndWithShadow={false}
        spaceBetweenTopAreaAndContent={false}
      >
        <div className={styles.channelGroupDetail}>
          <div className={styles.topArea}>
            <div className={styles.channelGroupName}>
              {i18n.t('channelGroupDetail.labels.name', {
                name: channelGroup.name
              })}
            </div>
            <div className={styles.buttonArea}>
              {renderArchiveButton()}
              <Link to={`${channelGroup.id}/edit`}>
                {i18n.t('channelGroups.titles.edit')}
              </Link>
            </div>
          </div>
          <Nav activeKey={tab} onSelect={onTabChange}>
            <Nav.Item>
              <Nav.Link eventKey={ChannelGroupTabs.BASIC}>
                {i18n.t('channelGroups.titles.basicTab')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={ChannelGroupTabs.REPORT}>
                {i18n.t('channelGroups.titles.reportTab')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={ChannelGroupTabs.AD_TYPES}>
                {i18n.t('channelGroups.titles.adTypesTab')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={ChannelGroupTabs.BID_PRICE}>
                {i18n.t('channelGroups.titles.bidPriceTab')}
              </Nav.Link>
            </Nav.Item>
            {channelGroup.channelType !== ChannelType.BEACON && (
              <Nav.Item>
                <Nav.Link eventKey={ChannelGroupTabs.SPECIAL_SETTINGS}>
                  {i18n.t('channelGroups.titles.specialSettingsTab')}
                </Nav.Link>
              </Nav.Item>
            )}
            {channelGroup.channelType !== ChannelType.BEACON && (
              <Nav.Item>
                <Nav.Link eventKey={ChannelGroupTabs.OUTDOOR_CREATIVE_SIZES}>
                  {i18n.t('channelGroups.titles.outdoorCreativeSizesTab')}
                </Nav.Link>
              </Nav.Item>
            )}
            {channelGroup.channelType !== ChannelType.BEACON && (
              <Nav.Item>
                <Nav.Link
                  eventKey={ChannelGroupTabs.NON_EXPRESS_PASS_CATEGORIES}
                >
                  {i18n.t('channelGroups.titles.nonExpressPassCategoriesTab')}
                </Nav.Link>
              </Nav.Item>
            )}
            {channelGroup.channelType === ChannelType.E_PAPER && (
              <Nav.Item>
                <Nav.Link eventKey={ChannelGroupTabs.EPAPER_DEFAULT_IMAGE}>
                  {i18n.t('channelGroups.titles.epaperDefaultImageTab')}
                </Nav.Link>
              </Nav.Item>
            )}
            {/* ECOPD-5701：操作歷程紀錄固定放最後。不另外加權限判斷 —— 這個畫面
                route 層就是 isRoles(sysAdmin)，與後端的 FUNC_SYS_ADMIN_MANAGEMENT 一致。 */}
            <Nav.Item>
              <Nav.Link eventKey={ChannelGroupTabs.AUDIT_LOG}>
                {i18n.t('channelGroups.titles.auditLogTab')}
              </Nav.Link>
            </Nav.Item>
          </Nav>
          {tab === ChannelGroupTabs.BASIC && (
            <div style={infoMarginTop}>
              <InfoDisplaySection
                data={basicInfo}
                title={i18n.t('channelGroups.titles.basicContent')}
              />
              {brandInfo && (
                <InfoDisplaySection
                  data={brandInfo}
                  title={i18n.t('channelGroups.titles.brandContent')}
                />
              )}
            </div>
          )}
          {tab === ChannelGroupTabs.SPECIAL_SETTINGS && (
            <div style={infoMarginTop}>
              <InfoDisplaySection
                data={specialSettingsInfo}
                title={i18n.t('channelGroups.titles.specialSettingsContent')}
              />
            </div>
          )}
          {tab === ChannelGroupTabs.REPORT && (
            <div style={infoMarginTop}>
              <InfoDisplaySection
                data={reportInfo}
                title={i18n.t('channelGroups.titles.reportContent')}
              />
              <InfoDisplaySection
                data={derecognitionAgencyInfo}
                title={i18n.t(
                  'channelGroups.titles.derecognitionAgencyContent'
                )}
              />
              <InfoDisplaySection
                data={currencyInfo}
                title={i18n.t('channelGroups.titles.currencyContent')}
              />
              <InfoDisplaySection
                data={defaultMarginInfo}
                title={i18n.t('channelGroups.titles.defaultMargin')}
              />
            </div>
          )}
          {tab === ChannelGroupTabs.BID_PRICE && renderBidPriceSettings()}
          {tab === ChannelGroupTabs.AD_TYPES && (
            <div style={infoMarginTop}>
              <InfoDisplaySection
                data={adTypesSettings}
                title={i18n.t('channelGroups.titles.adTypesContent')}
              />
            </div>
          )}
          {tab === ChannelGroupTabs.OUTDOOR_CREATIVE_SIZES && (
            <div style={infoMarginTop}>
              <InfoDisplaySection
                data={outdoorCreativeSizesInfo}
                title={i18n.t(
                  'channelGroups.titles.outdoorCreativeSizesContent'
                )}
              />
            </div>
          )}
          {tab === ChannelGroupTabs.NON_EXPRESS_PASS_CATEGORIES && (
            <div style={infoMarginTop}>
              <InfoDisplaySection
                data={nonExpressPassCategoriesInfo}
                title={i18n.t(
                  'channelGroups.titles.nonExpressPassCategoriesContent'
                )}
              />
            </div>
          )}
          {tab === ChannelGroupTabs.EPAPER_DEFAULT_IMAGE &&
            renderEpaperDefaultImage()}
          {tab === ChannelGroupTabs.AUDIT_LOG && (
            <div style={infoMarginTop}>
              <AuditLog model={auditLogModel} />
            </div>
          )}
        </div>
        <div className={styles.memberList}>
          <ChannelGroupMembers channelGroupId={channelGroup.id} />
        </div>
      </Page>
    </div>
  );
};
